import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, merge, Observable, Subscription } from 'rxjs';
import { UserSessionInfo } from '../models/frontend/user.session-info';
import { map, tap } from 'rxjs/operators';
import { CustomerVisibilityType } from '../models/request-and-responses/rest-api/enums/customerVisibility';

export interface ISessionStorageItem {
    readonly observable: Observable<string>;

    save(value: string): void;

    get(): string;

    remove(): void;

    isPresent(): boolean;

    toString(): string;
}

class SessionStorageItem implements ISessionStorageItem {
    private readonly bs: BehaviorSubject<string>;
    private readonly key: string;
    private pollingIntervalSubscription: Subscription;

    constructor(key: string) {
        this.key = key;
        this.bs = new BehaviorSubject<string>(sessionStorage.getItem(this.key));
        this.keepValueUpdated();
    }

    save(value: string): void {
        sessionStorage.setItem(this.key, value);
        this.updateValue();
    }

    get(): string {
        this.updateValue();
        return this.bs.getValue();
    }

    remove(): void {
        sessionStorage.removeItem(this.key);
        this.updateValue();
    }

    isPresent(): boolean {
        return this.get() != null;
    }

    toString(): string {
        return this.get();
    }

    get observable(): Observable<string> {
        return this.bs.asObservable();
    }

    /*registerEvent(eventName: string): SessionStorageItem {
        console.log(eventName);
        if (!Object.keys(this.EVENTS).includes(eventName)) {
            console.error(`Unknown event '${eventName}': missing key`);
            return;
        }
        this.EVENTS[eventName] = true;
        return this;
    }

    private shouldEmitEvent(eventName: string): boolean {
        if (!Object.keys(this.EVENTS).includes(eventName)) {
            console.warn(`Unknown event '${eventName}'. Nothing to emit.`);
            return false;
        }
        return this.EVENTS[eventName] === true;
    }*/

    private keepValueUpdated(): void {
        this.pollingIntervalSubscription = interval(1000)
            .pipe(tap(() => this.updateValue()))
            .subscribe();
    }

    private updateValue(): void {
        const newValue = sessionStorage.getItem(this.key);
        const oldValue = this.bs.getValue();
        if (oldValue !== newValue) {
            console.log(`Updating '${this.key}' value.`);
            this.bs.next(newValue);
        }
    }
}

export interface ITokenAndUserId {
    token: string;
    userId: string;
}

@Injectable()
export class SessionStorageService {
    static readonly role = new SessionStorageItem('role');
    static readonly id = new SessionStorageItem('id');
    static readonly permitContracts = new SessionStorageItem('permitContracts');
    static readonly username = new SessionStorageItem('username');
    static readonly agencyid = new SessionStorageItem('agencyid');
    static readonly canExportContract = new SessionStorageItem('canExportContract');
    static readonly canDoExportSemplice = new SessionStorageItem('canDoExportSemplice');
    static readonly canDoExportGas = new SessionStorageItem('canDoExportGas');
    static readonly canDoExportEe = new SessionStorageItem('canDoExportEe');
    static readonly email = new SessionStorageItem('email');
    static readonly jwt = new SessionStorageItem('jwt');
    static readonly brands = new SessionStorageItem('brands');
    static readonly customerVisibility = new SessionStorageItem('customerVisibility');
    static readonly optConcContract = new SessionStorageItem('optConcContract');
    static readonly teleselling = new SessionStorageItem('teleselling');
    static readonly cf = new SessionStorageItem('cf');
    static readonly fullName = new SessionStorageItem('fullName');
    static readonly canDoAdesioneADistanza = new SessionStorageItem('adesioneADistanza');
    static readonly canDoChiamataBenvenuto = new SessionStorageItem('chiamataBenvenuto');
    static readonly canDoPortaUnAmico = new SessionStorageItem('canDoPortaUnAmico');


    static TokenAndUserIdObservable = merge(
        SessionStorageService.jwt.observable,
        SessionStorageService.id.observable
    ).pipe(map(() => {
        return { token: SessionStorageService.jwt.get(), userId: SessionStorageService.id.get() };
    }));

    static get sessionInfo(): UserSessionInfo {

        function toBool(s: string, defaultValue: boolean) {
            try {
                if (s == null) {
                    return defaultValue;
                }
                return JSON.parse(s.toLowerCase()) === true;
            } catch (e) {
                console.warn(`Error parsing '${s}' to bool`, e);
                return defaultValue;
            }
        }

        return new UserSessionInfo({
            userId: Number(this.id.get()),
            role: Number(this.role.get()),
            permitContracts: Number(this.permitContracts.get()),
            canExportContract: JSON.parse(this.canExportContract.get()),
            canDoExportSemplice: JSON.parse(this.canDoExportSemplice.get()),
            canDoExportGas: JSON.parse(this.canDoExportGas.get()),
            canDoExportEe: JSON.parse(this.canDoExportEe.get()),
            email: this.email.get(),
            username: this.username.get(),
            customerVisibility: this.customerVisibility.get() as CustomerVisibilityType,
            permitBrands: JSON.parse(this.brands.get()),
            teleselling: JSON.parse(this.teleselling.get()),
            cf: this.cf.get(),
            fullName: this.fullName.get(),
            agencyId: Number(SessionStorageService.agencyid.get()),
            canDoAdesioneADistanza: toBool(this.canDoAdesioneADistanza.get(), false),
            canDoChiamataBenvenuto: toBool(this.canDoChiamataBenvenuto.get(), false),
            canDoPortaUnAmico: toBool(this.canDoPortaUnAmico.get(), false)
        });
    }

    static clear(): void {
        sessionStorage.clear();
    }

}



// WEBPACK FOOTER //
// ./src/app/services/session-storage.service.ts